import type { Booking, Resource, ProjectDetails, CostCentre } from '@/types';

interface BookingSearchLookups {
  resourcesById: Map<string, Resource>;
  projectDetailsById: Map<string, ProjectDetails>;
  costCentresById: Map<string, CostCentre>;
}

/**
 * The resources a booking is shown against: the timeline draws a multi-resource
 * booking on every id in `resourceIds`, so the search has to read all of them
 * and not only the primary one.
 */
const assignedResourceIds = (booking: Booking): string[] => {
  const ids = booking.resourceId != null ? [String(booking.resourceId)] : [];
  booking.resourceIds?.forEach(resourceId => {
    const id = String(resourceId);
    if (!ids.includes(id)) ids.push(id);
  });
  return ids;
};

/** Whether a booking should stay on the timeline for the quick-search query. */
export function bookingMatchesSearch(
  booking: Booking,
  query: string,
  { resourcesById, projectDetailsById, costCentresById }: BookingSearchLookups,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  const fields: (string | null | undefined)[] = [
    booking.projectName,
    booking.description,
    projectDetailsById.get(String(booking.projectId))?.billingCode,
    costCentresById.get(String(booking.costCentreId))?.code,
    costCentresById.get(String(booking.costCentreId))?.name,
  ];

  assignedResourceIds(booking).forEach(resourceId => {
    const resource = resourcesById.get(resourceId);
    fields.push(resource?.name, resource?.schedulerGroup, resource?.department);
  });

  return fields.some(field => !!field && field.toLowerCase().includes(needle));
}
